/**
 * Mapa Operacional — single pane of truth geoespacial de la Capa.
 *
 * Cablea los motores de mapa client-side (`../lib/mapLayers`,
 * `../lib/mapEngine`, `../lib/isolation`) y el Grafo_Entidades
 * (`../lib/graph`, resuelto vía el runtime selector `../api/runtime`)
 * sobre MapLibre GL, rellenando la vista con los datos reales de
 * Antofagasta (`/data/*.json`).
 *
 * Capacidades (R1, R2):
 *   - Capa_Mapa conmutables (DMAs, Gateways, Valvulas/PRV, Anomalias,
 *     Heatmap_NRW, Cuadrillas, Presion, fugas históricas); el toggle de
 *     una capa conserva el estado de las demás (R1.2, Property 2).
 *   - Clustering/declustering con conteo exacto al nivel de zoom vigente
 *     (R1.3, R1.4) vía `clusterFeatures` del ClusterEngine.
 *   - Selección con detalle del Grafo_Entidades (id, tipo, estado, ts del
 *     último evento y entidades relacionadas) vía `MapEngine.select`
 *     (R1.6).
 *   - Control de Playback_Temporal derivado de eventos (R2.1, R2.2).
 *   - Aislamiento_Valvular con impacto downstream (R2.4) y overlay de
 *     Simulacion_Hidraulica etiquetado "simulación" en capa separada
 *     (R2.5), sin mutar el estado real.
 *   - Lazy GIS por viewport: solo se clusteriza/renderiza lo visible.
 *
 * Sin tiles externos: fondo plano oscuro/claro y GeoJSON local. En
 * producción se cambia el `style` por un raster/MVT propio sin tocar la
 * lógica.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import maplibregl, { Map as MapLibreMap } from "maplibre-gl";

import { listDemoDmas } from "../api/mock";
import { loadRealData, type RealDma } from "../api/realData";
import { provideDemo, runtime } from "../api/runtime";
import type {
  CapaMapa,
  DetalleEntidad,
  EntidadRef,
  EventoOperacional,
  FiltroRealtime,
  RangoTemporal,
  ResultadoSimulado,
  ZonaAislada,
} from "../api/contracts";
import {
  buildGraphFromData,
  GrafoEntidadesLocal,
  nodeIdFor,
  type GraphData,
  type ProyeccionGrafo,
} from "../lib/graph";
import {
  deriveMapLayers,
  type CapaMapaGeografica,
  type ColeccionCapa,
  type FeatureMapa,
} from "../lib/mapLayers";
import {
  avanzarInstante,
  clusterFeatures,
  instanteVigente,
  MapEngine,
  playback,
  rangoValido,
  toggleCapa,
  UMBRAL_CLUSTER_DEFAULT,
  type ComandoPlayback,
  type EstadoPlayback,
} from "../lib/mapEngine";
import { isolate, simulate } from "../lib/isolation";
import { Icon } from "../components/Icon";
import { Ticker } from "../components/Ticker";
import { PageHeader } from "../layout/PageHeader";
import { useToast } from "../system/ToastContext";
import { useLiveDemo } from "../system/useLiveDemo";
import { exportToJson } from "../lib/exporters";

import "maplibre-gl/dist/maplibre-gl.css";

/**
 * Filtro de la Suscripcion_Realtime del Mapa_Operacional: refleja en vivo
 * los cambios geo-operacionales (anomalías, válvulas, cuadrillas, alertas)
 * sobre la red (R1.5, R7.4). Integración mínima vía Ticker compacto, sin
 * alterar la lógica del mapa.
 */
const FILTRO_MAPA: FiltroRealtime = {
  tipos: [
    "anomalia_detectada",
    "alerta_generada",
    "valvula_abierta",
    "valvula_cerrada",
    "cuadrilla_ubicacion",
  ],
};

// ---------------------------------------------------------------------------
// Metadatos de presentación de las Capa_Mapa
// ---------------------------------------------------------------------------

/** Capas geográficas renderizadas como puntos (clusterizables). */
const POINT_LAYERS: Array<{ id: CapaMapaGeografica; label: string; color: string }> = [
  { id: "dmas", label: "DMAs", color: "#6dd1ee" },
  { id: "gateways", label: "Gateways LoRaWAN", color: "#34d399" },
  { id: "valvulas", label: "Válvulas", color: "#8b95ec" },
  { id: "prvs", label: "PRVs", color: "#b78bec" },
  { id: "cuadrillas", label: "Cuadrillas", color: "#f5a524" },
  { id: "presion", label: "Presión", color: "#38bdf8" },
  { id: "anomalias", label: "Anomalías", color: "#f06464" },
  { id: "fugas_historicas", label: "Fugas históricas", color: "#ef4444" },
];

/** Color del overlay de simulación / aislamiento (capa separada, R2.5). */
const SIM_COLOR = "#ff4fd8";

const SRC_CLUSTERS = "op-clusters";
const SRC_HEATMAP = "op-heatmap";
const SRC_OVERLAY = "op-overlay";

const EMPTY_FC: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };

const DARK_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  glyphs: "https://fonts.openmaptiles.org/{fontstack}/{range}.pbf",
  sources: {
    "carto-dark": {
      type: "raster",
      tiles: [
        "https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
        "https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
        "https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
      ],
      tileSize: 256,
      attribution: "© OpenStreetMap, © CARTO",
    },
  },
  layers: [
    { id: "background", type: "background", paint: { "background-color": "#0a0f17" } },
    { id: "basemap", type: "raster", source: "carto-dark", paint: { "raster-opacity": 0.9 } },
  ],
};

const LIGHT_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  glyphs: "https://fonts.openmaptiles.org/{fontstack}/{range}.pbf",
  sources: {
    "carto-light": {
      type: "raster",
      tiles: [
        "https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png",
        "https://b.basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png",
        "https://c.basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png",
      ],
      tileSize: 256,
      attribution: "© OpenStreetMap, © CARTO",
    },
  },
  layers: [
    { id: "background", type: "background", paint: { "background-color": "#eef3f9" } },
    { id: "basemap", type: "raster", source: "carto-light", paint: { "raster-opacity": 1 } },
  ],
};

function readTheme(): "dark" | "light" {
  if (typeof window === "undefined") return "dark";
  return document.documentElement.getAttribute("data-theme") === "light" ? "light" : "dark";
}

/** Mapea una DemoDma al esquema `RealDma` que consumen los motores. */
function demoToRealDma(d: ReturnType<typeof listDemoDmas>[number]): RealDma {
  return {
    codigo: d.codigo,
    nombre: d.nombre,
    lat: d.lat,
    lon: d.lon,
    clientes: d.clientes,
    volumen_total: d.baseline * 1000,
    fugas: 0,
    backflow: 0,
    bateria: 0,
    nrw_pct: d.nrw_pct,
  };
}

type Bounds = [number, number, number, number]; // [oeste, sur, este, norte]

/** Filtra una colección a las Features dentro del viewport (lazy GIS). */
function featuresEnViewport(coleccion: ColeccionCapa, bounds: Bounds | null): FeatureMapa[] {
  if (!bounds) return coleccion.features;
  const [w, s, e, n] = bounds;
  return coleccion.features.filter((f) => {
    const [lon, lat] = f.geometry.coordinates;
    return lon >= w && lon <= e && lat >= s && lat <= n;
  });
}

// ---------------------------------------------------------------------------
// Datos derivados (grafo + capas) desde los JSONs reales de Antofagasta
// ---------------------------------------------------------------------------

interface DatosMapa {
  data: GraphData;
  grafo: ProyeccionGrafo;
  grafoLocal: GrafoEntidadesLocal;
  capas: Record<CapaMapaGeografica, ColeccionCapa>;
  exclusiones: number;
  eventos: EventoOperacional[];
}

/**
 * Sintetiza un log de Eventos_Operacionales a partir de las Anomalias
 * derivadas, para alimentar el Playback_Temporal (R2.1). En modo
 * conectado real estos eventos provienen del Event_Store; aquí se
 * derivan de los `ts` de las alarmas para tener una línea de tiempo real.
 */
function sintetizarEventos(capas: Record<CapaMapaGeografica, ColeccionCapa>): EventoOperacional[] {
  const eventos: EventoOperacional[] = [];
  let seq = 0;
  for (const f of capas.anomalias.features) {
    const ts = (f.properties["ts"] as string | undefined) ?? "";
    if (!ts) continue;
    eventos.push({
      event_id: `ev-${f.properties.id}`,
      tipo: "anomalia_detectada",
      entidad_ref: f.properties.entidad_ref,
      actor: "ingesta",
      ts,
      payload: { estado: f.properties["estado"] ?? "anomalia" },
      seq: ++seq,
      prev_hash: null,
      payload_hash: "",
      correlation_id: `corr-${f.properties.id}`,
    });
  }
  eventos.sort((a, b) => (a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0));
  return eventos;
}

async function buildDatosMapa(): Promise<DatosMapa> {
  const real = await loadRealData();
  const dmas: RealDma[] = real?.dmas?.length
    ? real.dmas
    : listDemoDmas().map(demoToRealDma);
  const data: GraphData = {
    dmas,
    gateways: real?.gateways ?? [],
    alarmas: real?.alarmas ?? [],
  };

  const grafo = buildGraphFromData(data);
  const grafoLocal = new GrafoEntidadesLocal(grafo);
  const { capas, exclusiones } = deriveMapLayers(data);
  const eventos = sintetizarEventos(capas);

  // Registra el Grafo_Entidades demo en el runtime selector para que la
  // selección lo consuma vía `runtime.graph()` (R17.1, R17.2).
  provideDemo("graph", () => grafoLocal);

  return { data, grafo, grafoLocal, capas, exclusiones: exclusiones.length, eventos };
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export function OperationalMap(): JSX.Element {
  const { toast } = useToast();

  // Stream en vivo de la Capa para el indicador "EN VIVO" y el Ticker
  // compacto (R1.5, R7.4); integración mínima que no altera el mapa.
  const { listo: liveListo } = useLiveDemo();

  const datosQuery = useQuery<DatosMapa>({
    queryKey: ["mapa-operacional.datos"],
    queryFn: buildDatosMapa,
    staleTime: 5 * 60_000,
  });

  // Capa_Mapa activas: todas las de estado real; `simulacion` aparte (R2.5).
  const [activas, setActivas] = useState<ReadonlySet<CapaMapa>>(
    () => new Set<CapaMapa>(POINT_LAYERS.map((l) => l.id).concat("heatmap_nrw" as CapaMapaGeografica)),
  );

  const [seleccion, setSeleccion] = useState<DetalleEntidad | null>(null);
  const [seleccionValvulas, setSeleccionValvulas] = useState<EntidadRef[]>([]);
  const [zonaAislada, setZonaAislada] = useState<ZonaAislada | null>(null);
  const [simulacion, setSimulacion] = useState<ResultadoSimulado | null>(null);

  const [zoom, setZoom] = useState(11.5);
  const [bounds, setBounds] = useState<Bounds | null>(null);

  const [playbackState, setPlaybackState] = useState<EstadoPlayback | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const mapReadyRef = useRef(false);

  const datos = datosQuery.data;

  // MapEngine: compone LayerManager + ClusterEngine + selección (R1.6).
  const engine = useMemo(() => {
    if (!datos) return null;
    return new MapEngine({ graph: runtime.graph(), profundidadVecinos: 1 });
  }, [datos]);

  // -------------------------------------------------------------------------
  // Conteos por capa (para la leyenda/panel)
  // -------------------------------------------------------------------------
  const conteos = useMemo(() => {
    const c: Record<string, number> = {};
    if (!datos) return c;
    for (const l of POINT_LAYERS) c[l.id] = datos.capas[l.id].features.length;
    c.heatmap_nrw = datos.capas.heatmap_nrw.features.length;
    return c;
  }, [datos]);

  // -------------------------------------------------------------------------
  // Clustering por capa activa, acotado al viewport (lazy GIS, R1.3/R1.4)
  // -------------------------------------------------------------------------
  const clusterFC = useMemo<GeoJSON.FeatureCollection>(() => {
    if (!datos) return EMPTY_FC;
    const features: GeoJSON.Feature[] = [];
    for (const l of POINT_LAYERS) {
      if (!activas.has(l.id)) continue;
      const visibles = featuresEnViewport(datos.capas[l.id], bounds);
      const clusters = clusterFeatures(
        { type: "FeatureCollection", features: visibles },
        zoom,
        UMBRAL_CLUSTER_DEFAULT,
      );
      for (const cl of clusters) {
        features.push({
          type: "Feature",
          geometry: { type: "Point", coordinates: [cl.coord.lon, cl.coord.lat] },
          properties: {
            capa: l.id,
            color: l.color,
            count: cl.count,
            es_cluster: cl.count > 1,
            id: cl.elementos?.[0] ?? `${l.id}-cluster`,
          },
        });
      }
    }
    return { type: "FeatureCollection", features };
  }, [datos, activas, zoom, bounds]);

  const heatmapFC = useMemo<GeoJSON.FeatureCollection>(() => {
    if (!datos || !activas.has("heatmap_nrw")) return EMPTY_FC;
    return datos.capas.heatmap_nrw as unknown as GeoJSON.FeatureCollection;
  }, [datos, activas]);

  // Overlay de simulación / aislamiento, en capa separada y etiquetada (R2.5).
  const overlayFC = useMemo<GeoJSON.FeatureCollection>(() => {
    if (!datos) return EMPTY_FC;
    const byNode = new Map<string, FeatureMapa>();
    for (const l of POINT_LAYERS) {
      for (const f of datos.capas[l.id].features) byNode.set(f.properties.node_id, f);
    }
    const refs: EntidadRef[] = [];
    if (zonaAislada) refs.push(...zonaAislada.zona);
    if (simulacion) refs.push(...simulacion.downstream.map((d) => d.entidad_ref));
    const features: GeoJSON.Feature[] = [];
    const vistos = new Set<string>();
    for (const ref of refs) {
      const node_id = nodeIdFor(ref);
      if (vistos.has(node_id)) continue;
      vistos.add(node_id);
      const f = byNode.get(node_id);
      if (!f) continue;
      features.push({
        type: "Feature",
        geometry: f.geometry,
        properties: { simulacion: true, tipo: ref.tipo, id: ref.id },
      });
    }
    return { type: "FeatureCollection", features };
  }, [datos, zonaAislada, simulacion]);

  // -------------------------------------------------------------------------
  // Inicialización del mapa MapLibre
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const initialTheme = readTheme();
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: initialTheme === "dark" ? DARK_STYLE : LIGHT_STYLE,
      center: [-70.4, -23.65], // Antofagasta
      zoom: 11.5,
      attributionControl: false,
    });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: false }), "top-right");

    const addSourcesAndLayers = (): void => {
      if (!map.getSource(SRC_HEATMAP)) {
        map.addSource(SRC_HEATMAP, { type: "geojson", data: EMPTY_FC });
        map.addLayer({
          id: "layer-heatmap",
          type: "heatmap",
          source: SRC_HEATMAP,
          paint: {
            "heatmap-weight": ["interpolate", ["linear"], ["coalesce", ["get", "weight"], 0], 0, 0, 50, 1],
            "heatmap-radius": 30,
            "heatmap-opacity": 0.55,
          },
        });
      }
      if (!map.getSource(SRC_CLUSTERS)) {
        map.addSource(SRC_CLUSTERS, { type: "geojson", data: EMPTY_FC });
        // Burbuja de cluster (count > 1).
        map.addLayer({
          id: "layer-cluster-circle",
          type: "circle",
          source: SRC_CLUSTERS,
          filter: ["==", ["get", "es_cluster"], true],
          paint: {
            "circle-color": ["get", "color"],
            "circle-opacity": 0.35,
            "circle-radius": ["interpolate", ["linear"], ["get", "count"], 2, 14, 1000, 34],
            "circle-stroke-color": ["get", "color"],
            "circle-stroke-width": 1.6,
          },
        });
        map.addLayer({
          id: "layer-cluster-count",
          type: "symbol",
          source: SRC_CLUSTERS,
          filter: ["==", ["get", "es_cluster"], true],
          layout: {
            "text-field": ["get", "count"],
            "text-font": ["Noto Sans Regular"],
            "text-size": 12,
          },
          paint: { "text-color": "#ffffff" },
        });
        // Elementos individuales (declustering).
        map.addLayer({
          id: "layer-point",
          type: "circle",
          source: SRC_CLUSTERS,
          filter: ["!=", ["get", "es_cluster"], true],
          paint: {
            "circle-color": ["get", "color"],
            "circle-radius": 5,
            "circle-stroke-color": "rgba(0,0,0,0.6)",
            "circle-stroke-width": 1,
          },
        });
      }
      if (!map.getSource(SRC_OVERLAY)) {
        map.addSource(SRC_OVERLAY, { type: "geojson", data: EMPTY_FC });
        map.addLayer({
          id: "layer-overlay",
          type: "circle",
          source: SRC_OVERLAY,
          paint: {
            "circle-color": SIM_COLOR,
            "circle-opacity": 0.45,
            "circle-radius": 9,
            "circle-stroke-color": SIM_COLOR,
            "circle-stroke-width": 2,
          },
        });
      }
    };

    map.on("load", () => {
      addSourcesAndLayers();
      mapReadyRef.current = true;
      setZoom(map.getZoom());
      const b = map.getBounds();
      setBounds([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
    });

    // Lazy GIS por viewport: recalcula zoom y bounds al moverse (R16.3).
    map.on("moveend", () => {
      setZoom(map.getZoom());
      const b = map.getBounds();
      setBounds([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
    });

    mapRef.current = map;

    const observer = new MutationObserver(() => {
      if (!mapRef.current) return;
      mapReadyRef.current = false;
      mapRef.current.setStyle(readTheme() === "dark" ? DARK_STYLE : LIGHT_STYLE);
      mapRef.current.once("style.load", () => {
        addSourcesAndLayers();
        mapReadyRef.current = true;
      });
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });

    return () => {
      observer.disconnect();
      mapRef.current?.remove();
      mapRef.current = null;
      mapReadyRef.current = false;
    };
  }, []);

  // -------------------------------------------------------------------------
  // Sincronización de fuentes GeoJSON con el estado de la vista
  // -------------------------------------------------------------------------
  const setData = useCallback((sourceId: string, data: GeoJSON.FeatureCollection): void => {
    const map = mapRef.current;
    if (!map) return;
    const apply = (): void => {
      const src = map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
      src?.setData(data);
    };
    if (mapReadyRef.current && map.isStyleLoaded() && map.getSource(sourceId)) apply();
    else map.once("idle", apply);
  }, []);

  useEffect(() => setData(SRC_CLUSTERS, clusterFC), [clusterFC, setData]);
  useEffect(() => setData(SRC_HEATMAP, heatmapFC), [heatmapFC, setData]);
  useEffect(() => setData(SRC_OVERLAY, overlayFC), [overlayFC, setData]);

  // Centrar en las DMAs reales cuando los datos cargan.
  useEffect(() => {
    if (!datos || !mapRef.current) return;
    const dmaFeatures = datos.capas.dmas.features;
    if (dmaFeatures.length === 0) return;
    const lons = dmaFeatures.map((f) => f.geometry.coordinates[0]);
    const lats = dmaFeatures.map((f) => f.geometry.coordinates[1]);
    const center: [number, number] = [
      lons.reduce((a, b) => a + b, 0) / lons.length,
      lats.reduce((a, b) => a + b, 0) / lats.length,
    ];
    mapRef.current.flyTo({ center, zoom: 11.5, speed: 1.4 });
  }, [datos]);

  // -------------------------------------------------------------------------
  // Selección de elementos (detalle del Grafo_Entidades, R1.6)
  // -------------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !datos || !engine) return;

    const byNode = new Map<string, FeatureMapa>();
    for (const l of POINT_LAYERS) {
      for (const f of datos.capas[l.id].features) byNode.set(f.properties.id, f);
    }

    const handler = (e: maplibregl.MapLayerMouseEvent): void => {
      const props = e.features?.[0]?.properties;
      if (!props) return;
      if (props.es_cluster) {
        // Zoom-in sobre un cluster → declustering progresivo (R1.4).
        map.easeTo({ center: e.lngLat, zoom: Math.min(map.getZoom() + 2, 18) });
        return;
      }
      const f = byNode.get(String(props.id));
      if (!f) return;
      void engine
        .select({
          node_id: f.properties.node_id,
          entidad_ref: f.properties.entidad_ref,
          estado: f.properties["estado"] as string | undefined,
        })
        .then((res) => {
          if (res.ok) setSeleccion(res.value);
        });
      // Si es una Válvula, permite añadirla al Aislamiento_Valvular (R2.4).
      if (f.properties.tipo === "Valvula") {
        setSeleccionValvulas((prev) =>
          prev.some((v) => v.id === f.properties.id)
            ? prev
            : [...prev, f.properties.entidad_ref],
        );
      }
    };

    map.on("click", "layer-point", handler);
    return () => {
      map.off("click", "layer-point", handler);
    };
  }, [datos, engine]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  /** Conmuta una Capa_Mapa conservando el estado de las demás (R1.2). */
  const onToggleCapa = useCallback((capa: CapaMapa): void => {
    setActivas((prev) => toggleCapa(prev, capa));
  }, []);

  /** Calcula el Aislamiento_Valvular e impacto downstream (R2.4). */
  const onIsolate = useCallback((): void => {
    if (!datos) return;
    if (seleccionValvulas.length === 0) {
      toast("Selecciona al menos una válvula en el mapa para aislar.", "warn");
      return;
    }
    const res = isolate(datos.grafo, seleccionValvulas);
    if (!res.ok) {
      setZonaAislada(null);
      toast(`No se pudo calcular el aislamiento: ${res.error}`, "danger");
      return;
    }
    setZonaAislada(res.value);
    setActivas((prev) => new Set(prev).add("simulacion"));
    toast(
      `Aislamiento: ${res.value.clientes_afectados.toLocaleString("es-CL")} clientes, ` +
        `${res.value.dmas_afectadas} DMAs, ${res.value.puntos_presion_afectados} puntos de presión.`,
      "info",
    );
  }, [datos, seleccionValvulas, toast]);

  /** Ejecuta una Simulacion_Hidraulica sin mutar el estado real (R2.3, R2.5). */
  const onSimulate = useCallback((): void => {
    if (!datos) return;
    if (seleccionValvulas.length === 0) {
      toast("Selecciona al menos una válvula para simular su cierre.", "warn");
      return;
    }
    const res = simulate(datos.grafo, {
      cambios: seleccionValvulas.map((entidad_ref) => ({ entidad_ref, cambio: { estado: "cerrada" } })),
    });
    if (!res.ok) {
      setSimulacion(null);
      toast(`La simulación no pudo completarse: ${res.error}`, "danger");
      return;
    }
    setSimulacion(res.value);
    setActivas((prev) => new Set(prev).add("simulacion"));
    toast(`Simulación: ${res.value.downstream.length} puntos de presión afectados (capa “simulación”).`, "success");
  }, [datos, seleccionValvulas, toast]);

  const onClearOverlay = useCallback((): void => {
    setZonaAislada(null);
    setSimulacion(null);
    setSeleccionValvulas([]);
    setActivas((prev) => {
      const next = new Set(prev);
      next.delete("simulacion");
      return next;
    });
  }, []);

  /** Inicia el Playback_Temporal sobre el rango total de eventos (R2.1, R2.7). */
  const onStartPlayback = useCallback((): void => {
    if (!datos || datos.eventos.length === 0) {
      toast("No hay eventos en el rango para reproducir.", "warn");
      return;
    }
    const rango: RangoTemporal = {
      inicio: datos.eventos[0].ts,
      fin: datos.eventos[datos.eventos.length - 1].ts,
    };
    if (!rangoValido(rango)) {
      toast("Rango temporal inválido: el inicio debe ser anterior al fin (R2.7).", "danger");
      return;
    }
    const res = playback(datos.eventos, rango);
    if (!res.ok) {
      toast(res.error, "danger");
      return;
    }
    setPlaybackState(res.value);
  }, [datos, toast]);

  const onPlaybackCmd = useCallback((cmd: ComandoPlayback): void => {
    setPlaybackState((prev) => (prev ? avanzarInstante(prev, cmd) : prev));
  }, []);

  const onStopPlayback = useCallback((): void => setPlaybackState(null), []);

  const instante = playbackState ? instanteVigente(playbackState) : null;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <>
      <PageHeader
        eyebrow="Mapa Operacional"
        title="Single pane of truth · red Antofagasta"
        subtitle="Capas conmutables, clustering, playback temporal, aislamiento valvular y simulación sobre datos reales."
        actions={
          <>
            <span
              className={`badge ${liveListo ? "badge--accent" : "badge--neutral"}`}
              title={liveListo ? "Stream operacional activo" : "Conectando al stream"}
            >
              <span
                style={{
                  display: "inline-block",
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  marginRight: 5,
                  background: liveListo ? "var(--c-success)" : "var(--c-text-faint)",
                }}
              />
              EN VIVO
            </span>
            <button
              className="btn"
              onClick={() => {
                if (!datos) return;
                exportToJson(`mapa-operacional-${new Date().toISOString().slice(0, 10)}.json`, {
                  generado: new Date().toISOString(),
                  capas_activas: [...activas],
                  conteos,
                  exclusiones: datos.exclusiones,
                  aislamiento: zonaAislada,
                  simulacion,
                });
                toast("Exportado snapshot del Mapa Operacional (JSON).", "success");
              }}
            >
              <Icon name="report" size={13} /> Exportar mapa
            </button>
          </>
        }
      />

      <div className="map-shell">
        {/* Panel izquierdo: capas conmutables (R1.1, R1.2) */}
        <aside className="map-panel">
          <div className="card__title" style={{ marginBottom: 8 }}>Capas operacionales</div>
          {POINT_LAYERS.map((def) => (
            <label key={def.id} className="layer-toggle">
              <span className="layer-toggle__name">
                <span className="map-legend__dot" style={{ background: def.color }} />
                {def.label}
              </span>
              <span className="layer-toggle__count">{conteos[def.id] ?? 0}</span>
              <button
                type="button"
                className="btn btn--ghost btn--icon"
                onClick={() => onToggleCapa(def.id)}
                aria-label={activas.has(def.id) ? "Ocultar" : "Mostrar"}
                aria-pressed={activas.has(def.id)}
              >
                <Icon name={activas.has(def.id) ? "shield" : "settings"} size={12} />
              </button>
            </label>
          ))}
          <label className="layer-toggle">
            <span className="layer-toggle__name">
              <span className="map-legend__dot" style={{ background: "#f97316" }} />
              Heatmap NRW
            </span>
            <span className="layer-toggle__count">{conteos.heatmap_nrw ?? 0}</span>
            <button
              type="button"
              className="btn btn--ghost btn--icon"
              onClick={() => onToggleCapa("heatmap_nrw")}
              aria-label={activas.has("heatmap_nrw") ? "Ocultar" : "Mostrar"}
              aria-pressed={activas.has("heatmap_nrw")}
            >
              <Icon name={activas.has("heatmap_nrw") ? "shield" : "settings"} size={12} />
            </button>
          </label>

          {/* Playback_Temporal (R2.1, R2.2) */}
          <div className="card__title" style={{ marginTop: 16 }}>Playback temporal</div>
          {!playbackState ? (
            <button className="btn" style={{ width: "100%" }} onClick={onStartPlayback}>
              <Icon name="clock" size={13} /> Reproducir eventos
            </button>
          ) : (
            <div className="vstack" style={{ gap: 6 }}>
              <div className="alert-component">
                <span>Instante</span>
                <strong style={{ fontVariantNumeric: "tabular-nums" }}>
                  {instante ? new Date(instante.ts).toLocaleString("es-CL") : "—"}
                </strong>
              </div>
              <div className="hstack" style={{ gap: 6 }}>
                <button className="btn btn--icon" onClick={() => onPlaybackCmd({ tipo: "prev" })} aria-label="Anterior">
                  ‹
                </button>
                <button
                  className="btn btn--icon"
                  onClick={() => onPlaybackCmd({ tipo: playbackState.reproduciendo ? "pause" : "play" })}
                  aria-label={playbackState.reproduciendo ? "Pausar" : "Reproducir"}
                >
                  {playbackState.reproduciendo ? "❚❚" : "▶"}
                </button>
                <button className="btn btn--icon" onClick={() => onPlaybackCmd({ tipo: "next" })} aria-label="Siguiente">
                  ›
                </button>
                <button className="btn btn--ghost" onClick={onStopPlayback}>Cerrar</button>
              </div>
              <span className="kpi__hint">
                {playbackState.instantes.length > 0
                  ? `${playbackState.indice + 1} / ${playbackState.instantes.length} instantes`
                  : "Sin instantes en el rango"}
              </span>
            </div>
          )}

          {/* Ticker compacto: eventos geo-operacionales en vivo (R1.5, R7.4).
              Integración mínima; no interfiere con la lógica del mapa. */}
          {liveListo && (
            <div style={{ marginTop: 16 }}>
              <Ticker
                filtro={FILTRO_MAPA}
                titulo="Eventos en vivo"
                variant="compact"
                maxVisibles={15}
              />
            </div>
          )}
        </aside>

        {/* Lienzo del mapa */}
        <div className="map-canvas" ref={containerRef}>
          {datosQuery.isLoading && (
            <div className="map-legend" style={{ top: 12, bottom: "auto" }}>
              Cargando red…
            </div>
          )}
          <div className="map-legend">
            <div style={{ fontWeight: 600, color: "var(--c-text-strong)" }}>Clustering</div>
            <div className="map-legend__row">
              <span className="map-legend__dot" style={{ background: "#6dd1ee" }} />
              <span>Burbujas = conteo exacto agrupado</span>
            </div>
            <div className="map-legend__row">
              <span className="map-legend__dot" style={{ background: SIM_COLOR }} />
              <span>Capa “simulación” / aislamiento</span>
            </div>
            <div className="map-legend__row">
              <span style={{ fontSize: 11, color: "var(--c-text-muted)" }}>
                Zoom {zoom.toFixed(1)} · umbral {UMBRAL_CLUSTER_DEFAULT}
              </span>
            </div>
          </div>
        </div>

        {/* Panel derecho: selección + aislamiento/simulación */}
        <aside className="map-panel">
          <div className="card__title">Detalle de selección</div>
          {seleccion ? (
            <div className="vstack" style={{ gap: 6 }}>
              <div className="kpi" style={{ marginBottom: 4 }}>
                <span className="kpi__label">{seleccion.tipo} · {seleccion.id}</span>
                <span className="kpi__value" style={{ fontSize: 16 }}>{seleccion.estado}</span>
                <span className="kpi__hint">
                  Último evento: {seleccion.ultimo_evento_ts
                    ? new Date(seleccion.ultimo_evento_ts).toLocaleString("es-CL")
                    : "sin registro"}
                </span>
              </div>
              <div className="alert-component">
                <span>Entidades relacionadas (Grafo)</span>
                <strong>{seleccion.relacionados.length}</strong>
              </div>
              <div className="vstack" style={{ gap: 2, maxHeight: 140, overflowY: "auto" }}>
                {seleccion.relacionados.slice(0, 12).map((r) => (
                  <span key={`${r.tipo}:${r.id}`} className="kpi__hint">
                    {r.tipo} · {r.id}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <span className="kpi__hint">Selecciona un elemento del mapa para ver su detalle y vecinos del Grafo_Entidades.</span>
          )}

          <div className="card__title" style={{ marginTop: 16 }}>Aislamiento valvular</div>
          <div className="alert-component">
            <span>Válvulas seleccionadas</span>
            <strong>{seleccionValvulas.length}</strong>
          </div>
          {seleccionValvulas.length > 0 && (
            <div className="vstack" style={{ gap: 2, marginBottom: 8 }}>
              {seleccionValvulas.map((v) => (
                <span key={v.id} className="kpi__hint">{v.id}</span>
              ))}
            </div>
          )}
          <div className="hstack" style={{ gap: 6 }}>
            <button className="btn btn--primary" style={{ flex: 1 }} onClick={onIsolate}>
              <Icon name="shield" size={13} /> Aislar
            </button>
            <button className="btn" style={{ flex: 1 }} onClick={onSimulate}>
              <Icon name="flask" size={13} /> Simular
            </button>
          </div>

          {zonaAislada && (
            <div className="vstack" style={{ gap: 4, marginTop: 10 }}>
              <span className="badge badge--warn">Impacto downstream</span>
              <div className="alert-component">
                <span>Clientes afectados</span>
                <strong>{zonaAislada.clientes_afectados.toLocaleString("es-CL")}</strong>
              </div>
              <div className="alert-component">
                <span>DMAs afectadas</span>
                <strong>{zonaAislada.dmas_afectadas}</strong>
              </div>
              <div className="alert-component">
                <span>Puntos de presión</span>
                <strong>{zonaAislada.puntos_presion_afectados}</strong>
              </div>
            </div>
          )}

          {simulacion && (
            <div className="vstack" style={{ gap: 4, marginTop: 10 }}>
              <span className="badge badge--accent">Resultado simulación</span>
              <span className="kpi__hint">
                {simulacion.downstream.length} puntos estimados — sin mutar el estado real (R2.3).
              </span>
            </div>
          )}

          {(zonaAislada || simulacion || seleccionValvulas.length > 0) && (
            <button className="btn btn--ghost" style={{ marginTop: 10, width: "100%" }} onClick={onClearOverlay}>
              Limpiar simulación
            </button>
          )}
        </aside>
      </div>
    </>
  );
}
